import * as React from "react";
import { Container } from "react-bootstrap";
import { shallowEqual, useDispatch } from "react-redux";
import { useToasts } from "react-toast-notifications";

import { parseVuplexEvent, conditionalLog } from "../libs/utilsLib";
import { setIsInSession, setIsKioskMode, setKiosk, setMobileDevice, setSelectedOrganization, setIsSpirometerConnected, setResetBluetoothCompleted } from "../redux/kiosk/actionCreators";
import { useAppSelector } from "../redux/store";
import { Kiosk, KioskSession, MobileDevice, PatientForKiosk } from "../Types/Common/KioskTypes";
import { OrganizationType, Program, Provider, SpirometerID } from "../Types/Common/Types";
import { NioxFenoMessagePrefix, sendMessageToCSharp } from "../libs/kioskLib";
import { FenoDeviceSerialNumber } from "../Types/Common/BrandedTypes";
import { NioxInstrumentInfoReport, NioxMeasurement, NioxMode, NioxStatusReport } from "../Types/Common/NioxFeno";
import { setNioxFenoInitialized, setNioxFenoStatusReport, setNioxFenoView } from "../redux/niox-feno/actionCreators";
import { dispatchNioxFenoCustomEvent } from "../libs/niox-feno/custom-events";
import { uploadNioxFenoInstrumentInfoReport } from "../libs/niox-feno/api";
import { NioxIPCServerVersion } from "../libs/niox-feno/constants";
import { setOscillometryTestStarted, setOscillometryResult, setOscillometryError, setOscillometryView } from "../redux/oscillometry/actionCreators";
import { OscillometryStartTestMessage, OscillometryCancelTestMessage, OscillometryTestStartedMessage, OscillometryTestCompleteMessage, OscillometryTestFailedMessage } from "../libs/oscillometry/types";
import { UploadNioxFenoMeasurementBody } from "../Types/Common/API/niox-feno-kiosk";
import { OfflineFenoEntry } from "../libs/niox-feno/req-res";

const { useEffect, useRef } = React;

type CalibrationStartMessage = {
  syringeVolumeInLiters: number;
  turbineIsReusable: boolean;
  isCalibrationCheck: boolean;
  slowFlowWeight: number;
  medFlowWeight: number;
  fastFlowWeight: number;
};

interface VuplexListenerProps { }

// VUPLEX IS HOW THE KIOSK MOBILE APP COMMUNICATES WITH THE IN-APP BROWSER
export const VuplexListener: React.FC<VuplexListenerProps> = ({ children }) => {
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const { isKioskMode, mobileDevice } = useAppSelector(state => state.kiosk);
  const { pairedFenoDeviceSerialNumber } = useAppSelector(state => state.nioxFeno);
  const currentRequestId = useAppSelector(state => state.oscillometry.currentRequestId);
  const translationText = useAppSelector(state => state.translations, shallowEqual);
  const selectedOrganization = useAppSelector(state => state.kiosk.selectedOrganization);
  const program = useAppSelector(state =>
    selectedOrganization?.program
      ? state.programs[selectedOrganization.program.toUpperCase()]
      : undefined
  );
  const enableInstantQc = program?.featureSet?.enableInstantQc ?? false;

  const addToastRef = useRef(addToast);
  const isKioskModeRef = useRef(isKioskMode);
  const mobileDeviceRef = useRef(mobileDevice);
  const translationTextRef = useRef(translationText);
  const pairedFenoDeviceSerialNumberRef = useRef(pairedFenoDeviceSerialNumber);
  const currentRequestIdRef = useRef(currentRequestId);
  const enableInstantQcRef = useRef(enableInstantQc);

  useEffect(() => {
    addToastRef.current = addToast;
    isKioskModeRef.current = isKioskMode;
    mobileDeviceRef.current = mobileDevice;
    translationTextRef.current = translationText;
    pairedFenoDeviceSerialNumberRef.current = pairedFenoDeviceSerialNumber;
    currentRequestIdRef.current = currentRequestId;
    enableInstantQcRef.current = enableInstantQc;
  }, [addToast, isKioskMode, mobileDevice, translationText, pairedFenoDeviceSerialNumber, currentRequestId, enableInstantQc]);

  useEffect(() => {
    const vuplexMessageListener = (event: any) => {
      // console.log({ event })
      const parsedEvent = parseVuplexEvent(event);
      if (!parsedEvent) return;

      conditionalLog(true, parsedEvent.type, parsedEvent.message);

      switch (parsedEvent.type) {
        case "mobileDevice":
          if (!mobileDeviceRef.current) dispatch(setMobileDevice(parsedEvent.message));
          break;
        case "organization":
          dispatch(setSelectedOrganization(parsedEvent.message));
          break;
        case "kiosk":
          dispatch(setKiosk(parsedEvent.message));
          break;
        case "inSession":
          dispatch(setIsInSession(parsedEvent.message));
          break;
        case "spirometerConnected":
          addToastRef.current(
            <Container>
              {translationTextRef.current.spirometer || "Spirometer"} {translationTextRef.current.connected || "Connected"}: {parsedEvent.message}
            </Container>,
            { autoDismiss: true, appearance: "info" }
          );
          dispatch(setIsSpirometerConnected(true));
          break;
        case "spirometerDisconnected":
          addToastRef.current(
            <Container>
              {translationTextRef.current.spirometer || "Spirometer"} {translationTextRef.current.disconnected || "Disconnected"}
            </Container>,
            { autoDismiss: true, appearance: "warning" }
          );
          dispatch(setIsSpirometerConnected(false));
          break;
        case "spirometerConnectionStatus":
          // message of this type is true/false - this message is to check the connection when returning from a session and to set it into redux
          if (parsedEvent.message) {
            dispatch(setIsSpirometerConnected(true));
          } else {
            dispatch(setIsSpirometerConnected(false));
          }
          break;
        case "resetBluetoothComplete":
          dispatch(setResetBluetoothCompleted(true));
          break;
        case "NioxFenoInitialized":
          dispatch(setNioxFenoInitialized());
          break;
        case "NioxFenoIPCServerVersion":
          if (parsedEvent.message !== NioxIPCServerVersion) {
            dispatch(setNioxFenoView("INITIALIZE_FAILURE"));
          } else {
            dispatch(setNioxFenoView("CONNECT"));
          }
          break;
        case "NioxFenoBTConnectionSuccessful":
          if (parsedEvent.message) {
            // If we don't have a pairedFenoDeviceSerialNumber but we received this message,
            // that (currently, as things stand at the time of writing) indicates that
            // the connection was cancelled before the Dash received this message.
            if (!pairedFenoDeviceSerialNumberRef.current) {
              sendMessageToCSharp("NioxFenoCancelConnection", true);
              return;
            }
            dispatch(setNioxFenoView("CONFIRMING_CONNECTION"));
          } else {
            dispatch(setNioxFenoView("CONNECTION_FAILED"));
          }
          break;
        case "NioxFenoStatusReport":
          dispatch(setNioxFenoStatusReport(parsedEvent.message));
          break;
        case "NioxFenoInstrumentInfoReport":
          if (parsedEvent.message) {
            // Not awaiting intentionally so the listener doesn't have to be async
            uploadNioxFenoInstrumentInfoReport(parsedEvent.message);
          }
          break;
        case "NioxFenoStartedMeasurement":
          dispatchNioxFenoCustomEvent.startedMeasurement(parsedEvent.message || false);
          break;
        case "NioxFenoLatestMeasurement":
          dispatchNioxFenoCustomEvent.latestMeasurement(parsedEvent.message);
          break;
        case "NioxFenoPastMeasurements":
          dispatchNioxFenoCustomEvent.pastMeasurements(parsedEvent.message);
          break;
        case "NioxFenoMeasurements":
          const offlineMeasurements: UploadNioxFenoMeasurementBody[] =
            parsedEvent.message?.measurements?.map((message: string) => JSON.parse(message)) ?? [];
          dispatchNioxFenoCustomEvent.requestOfflineMeasurements(offlineMeasurements);
          break;
        case "ThorasysOscTestStarted":
          if (currentRequestIdRef.current && parsedEvent.message.requestId !== currentRequestIdRef.current) {
            console.warn("[Oscillometry] RequestId mismatch - ignoring stale TEST_STARTED message", {
              expected: currentRequestIdRef.current,
              received: parsedEvent.message.requestId,
            });
            break;
          }
          console.log("[Oscillometry] Test Started: ", parsedEvent.message);
          dispatch(setOscillometryTestStarted(parsedEvent.message.timestamp));
          dispatch(setOscillometryView("TEST_IN_PROGRESS"));
          break;
        case "ThorasysOscTestComplete":
          if (currentRequestIdRef.current && parsedEvent.message.requestId !== currentRequestIdRef.current) {
            console.warn("[Oscillometry] RequestId mismatch - ignoring stale TEST_COMPLETE message", {
              expected: currentRequestIdRef.current,
              received: parsedEvent.message.requestId,
            });
            break;
          }
          console.log("[Oscillometry] Test Complete - Full Payload:", parsedEvent.message);
          dispatch(setOscillometryResult(parsedEvent.message));
          dispatch(setOscillometryView(enableInstantQcRef.current ? "QC_REVIEW" : "COMPLETE"));
          break;
        case "ThorasysOscTestFailed":
          if (currentRequestIdRef.current && parsedEvent.message.requestId !== currentRequestIdRef.current) {
            console.warn("[Oscillometry] RequestId mismatch - ignoring stale TEST_FAILED message", {
              expected: currentRequestIdRef.current,
              received: parsedEvent.message.requestId,
            });
            break;
          }
          dispatch(setOscillometryError(parsedEvent.message.error));
          dispatch(setOscillometryView("ERROR"));
          break;

        // ZDP-2108
        case "calibrationVolume":
          console.log('[CALIDATA] Volume received', parsedEvent.message);
          break;
        case "calibrationFlowsPerRange":
          console.log('[CALIDATA] Flows per range received', parsedEvent.message);
          break;
        case "calibrationReachedRangeVolume":
          console.log('[CALIDATA] Reached range volume received', parsedEvent.message);
          break;
        case "calibrationChangePhase":
          console.log('[CALIDATA] Change phase received', parsedEvent.message);
          break;
        case "calibrationBtpsReceived":
          console.log('[CALIDATA] BTPS received', parsedEvent.message);
          break;
        case "calibrationAborted":
          console.log('[CALIDATA] Aborted received', parsedEvent.message);
          break;
        case "calibrationComplete":
          console.log('[CALIDATA] Complete received', parsedEvent.message);
          break;

        // Android
        case "calibrationProgression":
          console.log("[CALIDATA] Progression received", parsedEvent.message);
          break;

        default:
          break;
      }

      if (!isKioskModeRef.current) dispatch(setIsKioskMode(true));
    };

    const addVuplexMessageListener = () => {
      window?.vuplex?.addEventListener("message", vuplexMessageListener);
      console.log("Vuplex is now ready");
      sendMessageToCSharp("vuplexReady", true);
    };

    if (window?.vuplex) {
      addVuplexMessageListener();
    } else {
      window.addEventListener("vuplexready", addVuplexMessageListener, { once: true });
    }

    return () => {
      window?.vuplex?.removeEventListener("message", vuplexMessageListener);
      window.removeEventListener("vuplexready", addVuplexMessageListener);
    };
  }, [dispatch]);

  return <>{children}</>;
};

/**
 * Messages that actually need arguments should utilize an object.
 * This object is passed as JSON directly to the ZEPHYRxNioxService where it parses the arguments.
 *
 * For the most part the entire message JSON string (that we create on our side) simply
 * gets passed through to the ZEPHYRxNioxService, save for `RequestInitialize` and `Kill` that needs to control
 * the IPCClient/Server.
 */
// type ValidateVuplexNioxFenoSendArgument<T> = T extends [true] | [Record<string, unknown>] ? T : never;
type VuplexNioxFenoSendMessages = {
  RequestInitialize: [true | "nobreath"]; // Will need to adjust if any more FeNO devices are added
  RequestIPCServerVersion: [true];
  ConnectToDeviceBT: [{ fenoDeviceSerialNumber: FenoDeviceSerialNumber }];
  CancelConnection: [true];
  RequestStatusReport: [true];
  RequestInstrumentInfoReport: [true];
  StartMeasurement: [{ mode: NioxMode; patientID: string }]; // Mode and PatientID
  StopMeasurement: [true];
  RequestLatestMeasurement: [true];
  ClearClearableError: [true];
  ClearPatientID: [true];
  RequestPastMeasurements: [{ startSequenceNumber: number; count: number }]; // StartSequenceNumber and Count
  Reset: [true];
  Kill: [true];
};

/** Only first argument is sent to C#! Second, etc... can be used by Dashboard (like for the providerEmail). */
export type VuplexSendMessages = {
  vuplexReady: [true];
  requestMobileDevice: [true];
  logout: [true];
  requestSpirometerConnectionStatus: [true];
  kiosk: [kiosk: Kiosk, providerEmail: string | undefined];
  reset: [true];
  openLink: [url: string];
  requestKiosk: [true];
  organization: [organization: OrganizationType];
  patient: [patient: PatientForKiosk];
  provider: [provider: Provider];
  program: [program: Program];
  session: [session: KioskSession];
  returnToMobile: [true];
  requestOrganization: [true];
  StartCalibration: [message: CalibrationStartMessage];
  StopCalibration: [true];
  ThorasysOsc: [message: OscillometryStartTestMessage | OscillometryCancelTestMessage];
  NioxFenoStoreMeasurement: [measurementBody: OfflineFenoEntry];
  NioxFenoRequestMeasurements: [true];
  NioxFenoClearMeasurement: [measurementBody: OfflineFenoEntry];
} & {
  [K in keyof VuplexNioxFenoSendMessages as `${NioxFenoMessagePrefix}${K}`]: VuplexNioxFenoSendMessages[K];
};

export type VuplexReceiveMessages =
  | {
    type: "mobileDevice";
    message: MobileDevice;
  }
  | {
    type: "organization";
    message: OrganizationType;
  }
  | {
    type: "kiosk";
    message: Kiosk;
  }
  | {
    type: "inSession";
    message: boolean;
  }
  | {
    type: "spirometerConnected";
    message: SpirometerID;
  }
  | {
    type: "spirometerDisconnected";
    message: unknown;
  }
  | {
    type: "spirometerConnectionStatus";
    message: boolean;
  }
  | {
    type: "resetBluetoothComplete";
    message: unknown;
  }
  | {
    type: "NioxFenoInitialized";
    message: boolean;
  }
  | {
    type: "NioxFenoIPCServerVersion";
    message: string; // VERSION
  }
  | {
    type: "NioxFenoBTConnectionSuccessful";
    message: boolean | null;
  }
  | {
    type: "NioxFenoStatusReport";
    message: NioxStatusReport | null; // This could definitely be null
  }
  | {
    type: "NioxFenoInstrumentInfoReport";
    message: NioxInstrumentInfoReport | null;
  }
  | {
    type: "NioxFenoStartedMeasurement";
    message: boolean | null;
  }
  | {
    type: "NioxFenoLatestMeasurement";
    message: NioxMeasurement | null; // This could definitely be null
  }
  | {
    type: "NioxFenoPastMeasurements";
    message: (NioxMeasurement | null)[] | null; // Not only can this entire thing be null, but every measurement too.
  }
  | {
    type: "NioxFenoMeasurements";
    message: { measurements: string[] } | null;
  }
  | {
    type: "ThorasysOscTestStarted";
    message: OscillometryTestStartedMessage;
  }
  | {
    type: "ThorasysOscTestComplete";
    message: OscillometryTestCompleteMessage;
  }
  | {
    type: "ThorasysOscTestFailed";
    message: OscillometryTestFailedMessage;
  }
  | {
    type: "calibrationVolume";
    message: string;
  }
  | {
    type: "calibrationFlowsPerRange";
    message: string;
  }
  | {
    type: "calibrationReachedRangeVolume";
    message: string;
  }
  | {
    type: "calibrationChangePhase"
    message: string;
  }
  | {
    type: "calibrationBtpsReceived";
    message: string;
  }
  | {
    type: "calibrationAborted";
    message: string;
  }
  | {
    type: "calibrationComplete";
    message: string;
  }
  | {
    type: "calibrationProgression";
    message: string;
  };
